// Deep-import the concrete module, not the `@naumu/shared` barrel - the barrel
// re-exports every schemas/*.ts and drags zod (~299 KiB) into any chunk that
// touches it, including the marketing shell's.
import { isHeicType } from '@naumu/shared/types/attachment';

/** Path prefix of the attachment proxy that understands `?original`/`?thumbnail`. */
const ATTACHMENT_DOWNLOAD_PREFIX = '/api/attachments/download/';

/**
 * True only for URLs served by our own API, which is the only thing that reads
 * our query params.
 *
 * A presigned R2/S3 URL is signed over its exact query string, so appending
 * anything to it invalidates the SigV4 signature and the object 403s. That is
 * reachable: the files-library lightbox hands `attachmentDownloadUrl` a
 * presigned URL whenever a HEIC File row's `r2Key` is not an attachment key.
 */
function isOwnApiUrl(url: string): boolean {
	if (url.startsWith('/api/')) return true;
	const origin = typeof window === 'undefined' ? undefined : window.location?.origin;
	if (!origin) return false;
	try {
		const parsed = new URL(url, origin);
		return parsed.origin === origin && parsed.pathname.startsWith('/api/');
	} catch {
		return false;
	}
}

/**
 * The URL to fetch when the user asked to keep the file, not to look at it.
 *
 * `/api/attachments/download/{id}` redirects HEIC to a webp transcode so it
 * renders in every browser - right for an `<img>`, wrong for a download, which
 * would silently hand back a different file than the one that was uploaded.
 * `?original=true` opts back into the raw bytes.
 *
 * Every other type serves its own bytes already, so this is a no-op for them.
 * Copy-to-clipboard deliberately keeps using the plain `url`: the webp is what
 * the canvas can read.
 */
export function attachmentDownloadUrl(att: { url: string; fileType: string }): string {
	if (!isHeicType(att.fileType)) return att.url;
	// Anything that is not our proxy already points at real bytes and cannot
	// take a query param: a staged attachment previews from an object URL (which
	// resolves by exact string), and a File row can carry a presigned URL whose
	// signature covers its query string.
	if (!isOwnApiUrl(att.url)) return att.url;
	return `${att.url}${att.url.includes('?') ? '&' : '?'}original=true`;
}

/**
 * Grid-sized variant of an attachment preview URL.
 *
 * The list payloads hand HEIC images the bare `/api/attachments/download/{id}`
 * path, which resolves to the full 2560px webp transcode - a wasteful fetch for
 * a ~150px grid cell. `?thumbnail=preview` asks for the gallery-sized variant
 * the same endpoint generates lazily.
 *
 * Only for `<img>` in a grid. Never pass the result to the lightbox or a
 * download: `thumbnail` outranks `original` in the controller, so it would hand
 * back the thumbnail instead of the file.
 */
export function previewThumbnailUrl(previewUrl: string, mime: string): string {
	if (!isHeicType(mime)) return previewUrl;
	if (!previewUrl.startsWith(ATTACHMENT_DOWNLOAD_PREFIX)) return previewUrl;
	return `${previewUrl}${previewUrl.includes('?') ? '&' : '?'}thumbnail=preview`;
}
